import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Trash2, ImageOff } from "lucide-react";
import { useAccount, photoUrl, errMsg } from "@/lib/account";
import { AdminStories, AdminOffers, AdminPayments } from "@/components/AdminExtras";

const Stat = ({ k, v }) => (<div className="card-royal rounded-2xl p-5"><div className="font-display text-3xl text-amber-200" data-testid={`admin-stat-${k}`}>{v}</div><div className="text-xs uppercase tracking-widest text-slate-400 mt-1">{k.replace("_", " ")}</div></div>);

const ProfileRow = ({ p, onChange }) => {
  const { authed } = useAccount();
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card-royal rounded-2xl p-5" data-testid={`admin-profile-${p.id}`}>
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-[200px]">
          <Link to={`/profile/${p.id}`} className="font-display text-2xl hover:text-amber-200">{p.name}, {p.age}</Link>
          <p className="text-xs text-slate-400">{p.gender} · {p.religion} · {p.profession} · {p.city}</p>
          <p className="text-sm text-slate-300 mt-2 line-clamp-3">{p.about || "No bio"}</p>
        </div>
        <div className="flex gap-2">
          {!p.reviewed && <button onClick={() => act(() => authed("post", `/admin/profiles/${p.id}/approve`), "Approved")} data-testid={`admin-approve-${p.id}`} className="btn-gold px-4 py-2 rounded-full text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Approve</button>}
          <button onClick={() => window.confirm(`Remove ${p.name}'s profile?`) && act(() => authed("delete", `/admin/profiles/${p.id}`), "Profile removed")} data-testid={`admin-remove-${p.id}`} className="px-4 py-2 rounded-full text-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center gap-1"><Trash2 className="w-4 h-4" /> Remove</button>
        </div>
      </div>
      {p.photos?.length > 0 && (
        <div className="flex gap-3 mt-4 flex-wrap">
          {p.photos.map((u, i) => (
            <div key={u} className="relative w-24 h-24 rounded-xl overflow-hidden">
              <img src={photoUrl(u)} alt="" className="w-full h-full object-cover" />
              <button onClick={() => act(() => authed("post", `/admin/profiles/${p.id}/photos/delete`, { url: u }), "Photo removed")} data-testid={`admin-photo-remove-${p.id}-${i}`} className="absolute top-1 right-1 p-1.5 rounded-full bg-black/70 text-red-300"><ImageOff className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Users = () => {
  const { authed } = useAccount();
  const [d, setD] = useState({ roles: [], users: [] });
  const load = useCallback(() => authed("get", "/users").then(setD).catch(() => {}), [authed]);
  useEffect(() => { load(); }, [load]);
  const setRole = async (uid, role) => { try { await authed("patch", `/users/${uid}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card-royal rounded-2xl divide-y divide-slate-800" data-testid="admin-users">
      {d.users.map((u) => (
        <div key={u.user_id} className="p-4 flex flex-wrap items-center gap-3 text-sm">
          <span className="flex-1 min-w-[180px] truncate">{u.email || u.user_id}</span>
          <span className="text-slate-400">{u.plan || "no plan"} · ₹{u.balance ?? 0}</span>
          <select value={u.role} onChange={(e) => setRole(u.user_id, e.target.value)} className="field text-sm" style={{ width: "8rem" }} data-testid={`admin-role-${u.user_id}`}>
            {d.roles.map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
};

export default function Admin() {
  const { authed, isAdmin, loading } = useAccount();
  const [tab, setTab] = useState("pending");
  const [stats, setStats] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const load = useCallback(() => {
    authed("get", "/admin/overview").then(setStats).catch(() => {});
    if (!["users", "stories", "offers", "payments"].includes(tab)) authed("get", `/admin/profiles?status=${tab}`).then(setProfiles).catch(() => {});
  }, [authed, tab]);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  if (loading) return null;
  if (!isAdmin) return <p className="py-32 text-center text-slate-400" data-testid="admin-forbidden">This area is for site owners only.</p>;
  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-14" data-testid="admin-page">
      <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Owner dashboard</p>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3">Review & manage</h1>
      {stats && <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">{[["revenue", `₹${stats.revenue}`], ["members", stats.members], ["active_plans", stats.active_plans], ["pending_review", stats.pending_review]].map(([k, v]) => <Stat key={k} k={k} v={v} />)}</div>}
      <div className="flex flex-wrap gap-2 mt-10">
        {["pending", "approved", "all", "users", "payments", "stories", "offers"].map((t) => <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`px-5 py-2 rounded-full text-sm capitalize ${tab === t ? "btn-gold" : "btn-ghost"}`}>{t}</button>)}
      </div>
      <div className="space-y-4 mt-6">
        {tab === "users" ? <Users /> : tab === "payments" ? <AdminPayments /> : tab === "stories" ? <AdminStories /> : tab === "offers" ? <AdminOffers /> : profiles.length === 0 ? <p className="text-slate-500 py-10 text-center" data-testid="admin-empty">No profiles here.</p>
          : profiles.map((p) => <ProfileRow key={p.id} p={p} onChange={load} />)}
      </div>
    </div>
  );
}
